import { faMicrophoneSlash, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Color } from '@/constants/color';
import linkStyles from '@/constants/linkStyles';
import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';
import useMicPermission from '@/hooks/useMicPermission';
import Button from '@/ui/components/Button';
import Tooltip from '@/ui/components/Tooltip';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

import { PhoneNumberProps } from './types';

const CONFIRM_TIMEOUT_MS = 3000;

const PhoneNumber = ({
  phone,
  onCall,
  size = 'xs',
  showNumberInLabel = false,
  showLink = true,
  fullWidthOnMobile = false
}: PhoneNumberProps) => {
  const softPhone = useSoftPhone();
  const { micNotApproved, micMessage, openSettings, extensionSettingsUrl, siteSettingsUrl } =
    useMicPermission();
  const { number } = phone;
  const formattedNumber = usFormatPhoneNumber(number);
  const [confirming, setConfirming] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearConfirmTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetConfirming = useCallback(() => {
    clearConfirmTimeout();
    setConfirming(false);
  }, [clearConfirmTimeout]);

  const handleCallClick = useCallback(() => {
    if (confirming) {
      resetConfirming();
      onCall(number);
    } else {
      setConfirming(true);
      timeoutRef.current = setTimeout(resetConfirming, CONFIRM_TIMEOUT_MS);
    }
  }, [confirming, number, onCall, resetConfirming]);

  useEffect(() => clearConfirmTimeout, [clearConfirmTimeout]);

  const micTooltip = (
    <span className="flex flex-col gap-1">
      <span>{micMessage}</span>
      <span className="flex gap-2">
        {extensionSettingsUrl && (
          <button
            type="button"
            className="underline hover:text-gray-300"
            onClick={() => openSettings!(extensionSettingsUrl)}>
            Extension settings
          </button>
        )}
        <button
          type="button"
          className="underline hover:text-gray-300"
          onClick={() =>
            openSettings
              ? openSettings(siteSettingsUrl)
              : navigator.clipboard?.writeText(siteSettingsUrl).catch(() => {})
          }>
          Site settings
        </button>
      </span>
    </span>
  );

  return (
    <div className={`flex items-center gap-1 ${fullWidthOnMobile ? 'max-[500px]:w-full' : ''}`}>
      {showLink && (
        <a
          className={`mp-sensitive ${linkStyles}`}
          target="_blank"
          rel="noreferrer"
          href={`tel:${number}`}>
          {formattedNumber}
        </a>
      )}
      {softPhone === 'disabled-in-embed' && (
        <Tooltip tooltipContent="Phone dialer is not supported in this view" placement="top">
          <span className="ml-1.5 inline-flex items-center gap-1">
            <Button
              color={Color.GREEN}
              size="xs"
              disabled
              className="inline-flex items-center gap-1 opacity-40 cursor-not-allowed">
              <FontAwesomeIcon icon={faPhone} className="text-[10px]" />
              Call
            </Button>
          </span>
        </Tooltip>
      )}
      {isSoftPhoneActive(softPhone) && !micNotApproved && (
        <Button
          color={Color.GREEN}
          size={size}
          ariaLabel={confirming ? `Confirm call to ${formattedNumber}` : `Call ${formattedNumber}`}
          onClick={handleCallClick}
          className={`${showLink ? 'ml-1.5' : ''} ${showNumberInLabel ? 'mp-sensitive' : ''} inline-flex items-center gap-1 [a.pseudo-aircall-extension-button_~_&]:ml-6 ${fullWidthOnMobile ? 'max-[500px]:flex max-[500px]:w-full max-[500px]:justify-center' : ''}`}>
          <FontAwesomeIcon icon={faPhone} className="text-[10px]" />
          {confirming ? 'You sure?' : showNumberInLabel ? `Call ${formattedNumber}` : 'Call'}
        </Button>
      )}
      {isSoftPhoneActive(softPhone) && micNotApproved && (
        <Tooltip tooltipContent={micTooltip} placement="top" interactive>
          <span
            className={`${showLink ? 'ml-1.5' : ''} inline-flex cursor-pointer items-center gap-1 [a.pseudo-aircall-extension-button_~_&]:ml-6 ${fullWidthOnMobile ? 'max-[500px]:flex max-[500px]:w-full max-[500px]:justify-center' : ''}`}>
            <Button
              color={Color.GREEN}
              size={size}
              disabled
              className={`${showNumberInLabel ? 'mp-sensitive' : ''} inline-flex items-center gap-1 opacity-60 ${fullWidthOnMobile ? 'max-[500px]:flex max-[500px]:flex-1 max-[500px]:justify-center' : ''}`}>
              <FontAwesomeIcon icon={faPhone} className="text-[10px]" />
              {showNumberInLabel ? `Call ${formattedNumber}` : 'Call'}
            </Button>
            <FontAwesomeIcon icon={faMicrophoneSlash} className="text-[10px] text-red-500" />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default PhoneNumber;
